import * as React from "react";
import { IJsonDate, JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { CheckboxEditor } from "../../../../../../../react-core/Html/CheckboxEditor";
import { addYears } from "../../../../../../../react-core/utils/date";
import { RadiosEditor } from "../../../../../../../react-core/Html/RadiosEditor";
import { DatePicker } from "../../../../../../../react-core/Datepicker/DatePicker";
import { SelectEditor } from "../../../../../../../react-core/Html/SelectEditor";
import { datePickerOptions, FrequencyTypeArrays, FrequencyArraysMap, processTypeEnum, PaymentMethodArraysMap } from "../../Resource/Contents";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { RegularIncomePaymentAlertMsg } from "../../Resource/Contents";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { PaymentMethod } from "../../../../../../../Wrap/Constants/Enums/Domain/PaymentMethod";
import { DrawdownType } from "../../../../../../../Wrap/Constants/Enums/Drawdown/DrawdownType";
import { RegularCrystallisationDetails } from "../../../../../../../Wrap/Models/Domains/Drawdown/RegularCrystallisationDetails";
import { DrawdownAnnualIncome } from "../../../../../../../Wrap/Models/Domains/Drawdown/DrawdownAnnualIncome";
import { calculateAnnualIncomeAmount, getDaysUntilMinimumPaymentDate, isIncomeExhausted } from "./Helper/IncomePaymentHelper";
import { Frequency } from "../../../../../../../Wrap/Constants/Enums/Domain/Frequency";
import { PensionBase } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/PensionBase";
import { BusinessDaysAction } from "../../../../Common/BusinessDays/Actions/BusinessDaysAction";
import { showChooseAssetstoSell, getSellableAssets, needAssetsToSellForDrawdown } from "../DrawdownDetails/Helper/DrawdownDetailHelper";
import { ChooseAssetsToSellFor } from "../DrawdownDetails/Resource/Contents";
import { DrawdownAnnualAmount } from "../../../../../../../Wrap/Models/Domains/Drawdown/DrawdownAnnualAmount";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { EstimatedPAYE } from "./EstimatedPAYE";
import { AllowedPaymentMethodAction } from "../../Pension/PensionPayments/Actions/AllowedPaymentMethodAction";
import { PaymentSubType } from "../../../../../../../Wrap/Constants/Enums/Pension/Payments/PaymentSubType";
import { AllowedPaymentMethodModel } from "../../../../../../../Wrap/Models/Models/Pension/Payments/AllowedPaymentMethodModel";
import { ExcludeNonSelectiveMethod, GetAllowedMethods, GetDefaultMethod } from "../../Pension/PensionPayments/Helpers/AllowedPaymentMethodHelpers";
import { CountryCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CountryCode";
import { DrawdownPaymentDestinationAllocation } from "../../../../../../../Wrap/Models/Domains/Drawdown/DrawdownPaymentDestinationAllocation";
import { IncomePaymentDestination } from "../../../../../../../Wrap/Constants/Enums/Domain/IncomePaymentDestination";
import { ProductType } from "../../../../../../../Wrap/Constants/Enums/Domain/ProductType";
import { SubAccountType } from "../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { SubAccountTypeHelper } from "../../Helpers/SubAccountTypeHelper";
import { getQueryParameters } from "../../../../../../Shared/Wizards/Entities/QueryParametersHelper";
import { ChooseAssetsToSellAssets } from "../DrawdownDetails/Store/DrawdownCalculationStoreStatic";

interface IRegularIncomePaymentProps extends IProcessComponentProps {
    data: DrawdownAnnualIncome;
    onChange: (changes) => void;
    labels: any;
    messages: any;
    drawdownType: DrawdownType;
    availableCash?: any;
    taxFreeLumpSum?: any;
    shouldHidePaymentMethod?: boolean;
    hasInternationalBankAccountDestination?: boolean;
    regularCrystallisationDetails?: RegularCrystallisationDetails;
    pensionBase: PensionBase;
    processType?: processTypeEnum;
    isITFC: boolean;
    pensionYearEndDate?: IJsonDate;
    firstPaymentDateAtLeast?: IJsonDate;
    nextPensionYearMinimalPaymentDate?: IJsonDate;
    subaccountHierarchyid?: string;
    sellableAssets?: ChooseAssetsToSellAssets[];
}
interface IRegularIncomePaymentState {
    firstPaymentDateAlertDisplay?: boolean;
    endDateDisplay: boolean;
    daysFromNowValue?: Date | null;
    allowedPaymentMethods: AllowedPaymentMethodModel[];
    needToSellAssetsForDrawdown?: boolean;
    needToSellAssetsForIncome?: boolean;
    lastKnownPaymentMethod?: PaymentMethod;
}

export class RegularIncomePayment extends ProcessComponent<IRegularIncomePaymentProps, IRegularIncomePaymentState> {
    constructor(props: any) {
        super(props);
        this.state = {
            firstPaymentDateAlertDisplay: false,
            endDateDisplay: props.data?.PaymentEnd != null,
            daysFromNowValue: null,
            allowedPaymentMethods: [],
            needToSellAssetsForDrawdown: false,
            needToSellAssetsForIncome: false,
            lastKnownPaymentMethod: props.data?.PaymentMethod
        };
    }

    private isIncomeDisinvestmentCashOnly(
        disinvestmentAllocations: any[],
        assetDictionary: { [key: number]: ProductType } | undefined,
        isAcrossAllAssets?: boolean
    ): boolean {
        if (disinvestmentAllocations && disinvestmentAllocations.length > 0) {
            const toNumber = (v: any) => (typeof v === "number" ? v : Number(v) || 0);
            if (assetDictionary) {
                const nonCashPct = disinvestmentAllocations
                    .filter((a) => (a?.ProductId ?? 0) > 0 && assetDictionary[a.ProductId] !== ProductType.Cash)
                    .reduce((sum, a) => sum + toNumber(a?.Percentage ?? 0), 0);
                const cashPct = disinvestmentAllocations
                    .filter((a) => (a?.ProductId ?? 0) > 0 && assetDictionary[a.ProductId] === ProductType.Cash)
                    .reduce((sum, a) => sum + toNumber(a?.Percentage ?? 0), 0);
                if (nonCashPct > 0) {
                    return false;
                }
                return cashPct >= 100;
            } else {
                const onlyOne = disinvestmentAllocations.length === 1;
                const cashPct = disinvestmentAllocations
                    .filter((a) => a?.Type === ProductType.Cash)
                    .reduce((sum, a) => sum + toNumber(a?.Percentage ?? 0), 0);
                if (onlyOne && cashPct >= 100) {
                    return true;
                }
                return false;
            }
        }
        if (isAcrossAllAssets === true) {
            if (!assetDictionary) {
                return false;
            }
            const hasNonCashHolding = Object.values(assetDictionary).some((pt) => pt !== ProductType.Cash);
            return !hasNonCashHolding;
        }
        return false;
    }

    private initAssetDictionaryAndUpdateIncomeSellFlag(): void {
        if (this.props.sellableAssets && this.props.sellableAssets.length > 0) {
            const needIncomeSell = this.needToSellAssetsForIncome();

            this.setState({ needToSellAssetsForIncome: needIncomeSell }, () => {
                this.reCalculateNextMinimumPaymentDate();
            });
            return;
        }
    }

    async componentDidMount() {
        const {
            onChange,
            drawdownType,
            regularCrystallisationDetails,
            data,
        } = this.props;
        super.componentDidMount();

        const needToSellAssetsForDrawdown = (() => {
            const { processType, isITFC } = this.props;
            
            // NewBusiness or Quote: isITFC true then false
            if (processType === processTypeEnum.NewBusiness || processType === processTypeEnum.Quote) {
                return isITFC ? false : showChooseAssetstoSell(this.props.pensionBase?.DrawdownModel, ChooseAssetsToSellFor.Drawdown);
            }

            // standalone TPB
            if (processType === processTypeEnum.TakePensionBenefits) {
                return showChooseAssetstoSell(this.props.pensionBase?.DrawdownModel, ChooseAssetsToSellFor.Drawdown);
            }
            //in most case it is false
            return false;
        })();
        
        this.initAssetDictionaryAndUpdateIncomeSellFlag();
        
        this.setState({
            needToSellAssetsForDrawdown
        });

        this.getAllAllowedPaymentMethods();
        
        if (drawdownType === DrawdownType.Regular && regularCrystallisationDetails) {
            onChange({
                Frequency: data.Frequency ?? regularCrystallisationDetails.Frequency,
                PaymentMethod: data.PaymentMethod ? data.PaymentMethod : PaymentMethod.Bacs,
                NextPayment:
                    data.NextPayment && JsonDateParser.toDateNoOffset(data.NextPayment).getDate() <= 28
                        ? data.NextPayment
                        : regularCrystallisationDetails.FirstPaymentDay,
            });
        } else {
            var frequency =
                data.Frequency != undefined && data.Frequency != Frequency.NotSpecified
                    ? data.Frequency
                    : Frequency.Monthly;
            var paymentMethod = data.PaymentMethod ? data.PaymentMethod :
                                                    this.props.shouldHidePaymentMethod ?
                                                    PaymentMethod.NotSpecified : GetDefaultMethod(this.state.allowedPaymentMethods, this.getGrossIncomePayment());
            
        var daysUntilFirstPaymentDate = getDaysUntilMinimumPaymentDate(paymentMethod, needToSellAssetsForDrawdown, this.state.needToSellAssetsForIncome, data.TakeDifferentInitialIncomePayment);
            var nextPaymentDate = await this.daysFromNow(daysUntilFirstPaymentDate);
            
            onChange({
                Frequency: frequency,
                PaymentMethod: paymentMethod,
                NextPayment:
                    data.NextPayment && JsonDateParser.toDateNoOffset(data.NextPayment).getDate() <= 28
                        ? data.NextPayment
                        : JsonDateParser.toJsonDateNoOffset(nextPaymentDate ?? new Date()),
                AnnualAmount: data?.TargetSpecificArrangements ? data?.AnnualAmount : this.calculateAnnualAmount(data?.GrossIncomePerPayment?.Amount ?? 0, frequency)
            });
        }
    }

    componentDidUpdate(prevProps: Readonly<IRegularIncomePaymentProps>): void {
        // Check for external PaymentMethod changes (from IncomeBankDetails)
        const currentPaymentMethod = this.props.data?.PaymentMethod;
        if (currentPaymentMethod !== this.state.lastKnownPaymentMethod && 
            this.state.lastKnownPaymentMethod !== undefined) {
            this.setState({ lastKnownPaymentMethod: currentPaymentMethod });
            this.reCalculateNextMinimumPaymentDate();
        }

        // Update asset selling state if pensionBase.DrawdownModel changes
        if (prevProps.pensionBase?.DrawdownModel !== this.props.pensionBase?.DrawdownModel ||
            prevProps?.sellableAssets !== this.props?.sellableAssets) {
            this.updateNeedToSellAssetsState();
        }

        if (prevProps.shouldHidePaymentMethod !== this.props.shouldHidePaymentMethod ||
            prevProps?.hasInternationalBankAccountDestination !== this.props?.hasInternationalBankAccountDestination) {
            this.checkPaymentMethodOnConditionChange();
        }

        if (prevProps.data?.GrossIncomePerPayment?.Amount !== this.props.data?.GrossIncomePerPayment?.Amount ||
            prevProps.data?.GrossIncomePerPaymentFromPension?.Amount !== this.props.data?.GrossIncomePerPaymentFromPension?.Amount) {
            this.reCalculateNextMinimumPaymentDate_PaymentAmountChange();
        }

        if (prevProps.data?.PaymentMethod !== this.props.data?.PaymentMethod) {
            this.setState({ lastKnownPaymentMethod: this.props.data?.PaymentMethod });
        }

        if (prevProps.data?.PaymentMethod !== this.props.data?.PaymentMethod || prevProps.data?.TakeDifferentInitialIncomePayment !== this.props.data?.TakeDifferentInitialIncomePayment) {
            this.reCalculateNextMinimumPaymentDate();
        }
    }

    private getAllAllowedPaymentMethods(): void {
        AllowedPaymentMethodAction.GetAllAllowedPaymentMethods(this.props.subaccountHierarchyid, PaymentSubType.RegularPensionIncome, undefined, (allowedPaymentMethods) => {
            this.setState({ allowedPaymentMethods: ExcludeNonSelectiveMethod(allowedPaymentMethods) });
        });
    }

    private getGrossIncomePayment(): number {
        return this.props.data.TargetSpecificArrangements
            ? this.props.data?.GrossIncomePerPaymentFromPension?.Amount
            : this.props.data.GrossIncomePerPayment?.Amount || 0;
    }

    private updateNeedToSellAssetsState(): void {
        const needToSellAssetsForDrawdown = (() => {
            const { processType, isITFC } = this.props;
            if (processType === processTypeEnum.NewBusiness || processType === processTypeEnum.Quote) {
                return isITFC ? false : showChooseAssetstoSell(this.props.pensionBase?.DrawdownModel, ChooseAssetsToSellFor.Drawdown);
            }

            if (processType === processTypeEnum.TakePensionBenefits) {
                return showChooseAssetstoSell(this.props.pensionBase?.DrawdownModel, ChooseAssetsToSellFor.Drawdown);
            }

            return false;
        })();

        const needToSellAssetsForIncome = this.needToSellAssetsForIncome();
        if (
            this.state.needToSellAssetsForDrawdown !== needToSellAssetsForDrawdown ||
            this.state.needToSellAssetsForIncome !== needToSellAssetsForIncome
        ) {
            this.setState(
                {
                    needToSellAssetsForDrawdown,
                    needToSellAssetsForIncome,
                },
                () => {
                    this.reCalculateNextMinimumPaymentDate();
                }
            );
        }
    }

    private needToSellAssetsForIncome(): boolean {
        const dict: { [key: number]: ProductType } = {};
        if (this.props.sellableAssets && this.props.sellableAssets.length > 0) {
            this.props.sellableAssets.forEach((asset) => {
                if (asset?.Id) {
                    dict[asset.Id] = asset.ProductType;
                }
            });
        }

        const allocations = this.props.processType === processTypeEnum.EditIncomePayments ?
            this.props.pensionBase?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.IncomeDisinvestmentAllocations :
            this.props.pensionBase?.DrawdownModel?.IncomeDisinvestmentAllocations;

        const isAcrossAllAssets = this.props.pensionBase?.DrawdownModel?.IsAcrossAllAssets;

        const needToSellAssetsForIncome = !this.isIncomeDisinvestmentCashOnly(allocations, dict, isAcrossAllAssets);

        return needToSellAssetsForIncome;
    }

    private async daysFromNow(days): Promise<Date> {
        const now = new Date(Date.now());
        now.setHours(0, 0, 0);
        const resultDate = await this.getBusinessDays(now, true, days);

        resultDate.setHours(0, 0, 0);
        if (resultDate.getDate() > 28) {
            resultDate.setDate(1);
            resultDate.setMonth(resultDate.getMonth() + 1);
        }

        this.setState({
            daysFromNowValue: resultDate,
        });

        return resultDate;
    }

    private getBusinessDays(baseDate, future, numberOfDays): Promise<Date> {
        return new Promise((resolve) => {
            BusinessDaysAction.GetBusinessDays(baseDate, future, numberOfDays, true, (d) => {
                resolve(JsonDateParser.toDateNoOffset(d));
            });
        });
    }

    private calculateAnnualAmount(grossIncomePerPaymentAmount: number, frequency: Frequency): DrawdownAnnualAmount {
        if (
            grossIncomePerPaymentAmount != null &&
            grossIncomePerPaymentAmount != undefined &&
            frequency != null &&
            frequency != undefined
        ) {
            return { Amount: calculateAnnualIncomeAmount(grossIncomePerPaymentAmount, frequency) };
        }
        return { Amount: { Amount: 0, Currency: CurrencyCode.GBP } };
    }

    private getFirstPaymentDateAtLeast(): Date {
        var result = this.props.pensionYearEndDate
            ? addYears(JsonDateParser.toDate(this.props.pensionYearEndDate), 1)
            : this.state.daysFromNowValue;
        if (this.props.firstPaymentDateAtLeast) {
            let firstPaymentDateAtLeastFromProps = JsonDateParser.toDateNoOffset(this.props.firstPaymentDateAtLeast);
            result = result && firstPaymentDateAtLeastFromProps < result ? result : firstPaymentDateAtLeastFromProps;

            result.setHours(0, 0, 0);
            if (result.getDate() > 28)
            {
                result.setDate(1);
                result.setMonth(result.getMonth() + 1);
            }
        }
        if (this.props.nextPensionYearMinimalPaymentDate) {
            let nextPensionYearMinimalPaymentDateFromProps = JsonDateParser.toDateNoOffset(this.props.nextPensionYearMinimalPaymentDate);
            result = result && nextPensionYearMinimalPaymentDateFromProps < result ? result : nextPensionYearMinimalPaymentDateFromProps;
        }
        return result || new Date();
    }

    private async checkPaymentMethodOnConditionChange() {
        const paymentAmount = this.getGrossIncomePayment();
        const currentMethod = this.props.data?.PaymentMethod || PaymentMethod.NotSpecified;

        if (!this.props.shouldHidePaymentMethod) {
            if (this.props?.hasInternationalBankAccountDestination) {
                this.props.onChange(
                    {
                        PaymentMethod: PaymentMethod.Swift,
                    });
            }
            else {
                const allowedMethods = GetAllowedMethods(this.state.allowedPaymentMethods, paymentAmount, this.props.hasInternationalBankAccountDestination);
                if (!allowedMethods.includes(currentMethod)) {
                    const defaultMethod = GetDefaultMethod(this.state.allowedPaymentMethods, paymentAmount);
                    if (defaultMethod != currentMethod) {
                        this.props.onChange(
                            {
                                PaymentMethod: defaultMethod,
                            });
                    }
                }
            }
        }
        else if (currentMethod == PaymentMethod.InternalTransfer || currentMethod == PaymentMethod.Swift) {
            var daysUntilFirstPaymentDate = getDaysUntilMinimumPaymentDate(currentMethod, this.state.needToSellAssetsForDrawdown, this.state.needToSellAssetsForIncome, this.props.data?.TakeDifferentInitialIncomePayment);
            var nextPaymentDate2 = JsonDateParser.toJsonDateNoOffset(await this.daysFromNow(daysUntilFirstPaymentDate) ?? new Date());
            this.props.onChange(
                {
                    NextPayment: nextPaymentDate2
                });
        }
    }

    private async reCalculateNextMinimumPaymentDate_PaymentAmountChange() {
        const currentMethod = this.props.data?.PaymentMethod || PaymentMethod.NotSpecified;
        const needToSellAssetsForIncome = this.needToSellAssetsForIncome();

        if (this.state.needToSellAssetsForIncome == null || (this.state.needToSellAssetsForIncome != needToSellAssetsForIncome)) {
            var daysUntilFirstPaymentDate = getDaysUntilMinimumPaymentDate(currentMethod, this.state.needToSellAssetsForDrawdown, needToSellAssetsForIncome, this.props.data?.TakeDifferentInitialIncomePayment);
            var nextPaymentDate = JsonDateParser.toJsonDateNoOffset(await this.daysFromNow(daysUntilFirstPaymentDate) ?? new Date());
            this.props.onChange({ NextPayment: nextPaymentDate });

            this.setState({ needToSellAssetsForIncome: needToSellAssetsForIncome })
        }
    }

    private async reCalculateNextMinimumPaymentDate() {
        const paymentAmount = this.getGrossIncomePayment();
        const currentMethod = this.props.data?.PaymentMethod || PaymentMethod.NotSpecified;

        var daysUntilFirstPaymentDate = getDaysUntilMinimumPaymentDate(currentMethod, this.state.needToSellAssetsForDrawdown, this.state.needToSellAssetsForIncome, this.props.data?.TakeDifferentInitialIncomePayment);
        var nextPaymentDate = JsonDateParser.toJsonDateNoOffset(await this.daysFromNow(daysUntilFirstPaymentDate) ?? new Date());
        this.props.onChange({ NextPayment: nextPaymentDate });
    }

    public render(): JSX.Element {
        const {
            data,
            labels,
            messages,
            drawdownType,
            availableCash,
            taxFreeLumpSum,
            shouldHidePaymentMethod,
            regularCrystallisationDetails,
            onChange,
            pensionBase,
            processType,
            isITFC,
        } = this.props;

        let daysUntilFirstPaymentDate = 10;
        let firstPaymentDateMoreThanMessage = messages.firstPaymentDateUnder10;
        if (availableCash< taxFreeLumpSum) {
            daysUntilFirstPaymentDate = 15;
            firstPaymentDateMoreThanMessage = messages.firstPaymentDateUnder15;
        }

        const specifiedFirstPaymentDateAtLeast = this.getFirstPaymentDateAtLeast();
        if (data?.NextPayment && JsonDateParser.toDate(data?.NextPayment) < specifiedFirstPaymentDateAtLeast) 
        {
            data.NextPayment = JsonDateParser.toJsonDateNoOffset(specifiedFirstPaymentDateAtLeast);
        }

        if (this.props.nextPensionYearMinimalPaymentDate &&
            JsonDateParser.toDateNoOffset(this.props.nextPensionYearMinimalPaymentDate) >= specifiedFirstPaymentDateAtLeast) {
            firstPaymentDateMoreThanMessage = messages.nextPensionYearMinimalPaymentDate + JsonDateParser.getFormatDate(this.props.nextPensionYearMinimalPaymentDate, "dd/MM/yyyy");
        }

        let defaultFrequency =
            drawdownType === DrawdownType.Regular
            ? regularCrystallisationDetails?.Frequency
            : data?.Frequency;

        if (defaultFrequency === Frequency.NotSpecified) {
            defaultFrequency = FrequencyTypeArrays[0];
        }

        return (
            <>
                <h3>Income</h3>
                <div className="row">
                    <div className="col-4">
                        <SelectEditor
                            label={labels.incomeFrequency}
                            options={FrequencyTypeArrays}
                            selected={defaultFrequency}
                            getText={(v) => {
                                return FrequencyArraysMap[v];
                            }}
                            required={drawdownType !== DrawdownType.Regular && messages.incomeFrequencyRequired}
                            disabled={drawdownType === DrawdownType.Regular}
                            onChange={(v) => {
                                if (drawdownType !== DrawdownType.Regular) {
                                    onChange({
                                        Frequency: v,
                                        AnnualAmount: this.calculateAnnualAmount(
                                            data.GrossIncomePerPayment?.Amount ?? 0,
                                            v
                                        ),
                                    });
                                }
                            }}
                        />
                    </div>
                </div>
                {drawdownType === DrawdownType.Regular && (
                    <Alert message={RegularIncomePaymentAlertMsg.Frenquency} alertType={AlertType.Warning} />
                )}
                {isIncomeExhausted(drawdownType, data, pensionBase, processType, isITFC) && (
                    <>
                        <div className="row">
                            <div className="col-12">
                                <Alert
                                    data-test="flexiArrangementsExhausted"
                                    message={messages.flexiArrangementsExhausted}
                                    alertType={AlertType.Warning}
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-12">
                                <CheckboxEditor
                                    value={data?.FlexiArrangementsExhaustedConfirmed || false}
                                    onChange={(v) => {
                                        onChange({ FlexiArrangementsExhaustedConfirmed: v });
                                    }}
                                    label={labels.flexiArrangementsExhausted}
                                    validationRules={[
                                        {
                                            errorMessage:
                                                "Please confirm that your client is happy to proceed with their post-retirement arrangment value being exhausted within a year",
                                            validator: (v) => {
                                                return (
                                                    isIncomeExhausted(
                                                        drawdownType,
                                                        data,
                                                        pensionBase,
                                                        processType,
                                                        isITFC
                                                    ) && v
                                                );
                                            },
                                        },
                                    ]}
                                />
                            </div>
                        </div>
                    </>
                )}
                {!shouldHidePaymentMethod && (
                    <div className="row">
                        <div className="col-4">
                            <SelectEditor
                                label={labels.paymentMethod}
                                options={GetAllowedMethods(this.state.allowedPaymentMethods, this.getGrossIncomePayment(), this.props.hasInternationalBankAccountDestination)}
                                selected={data?.PaymentMethod ?? GetDefaultMethod(this.state.allowedPaymentMethods, this.getGrossIncomePayment())}
                                getText={(v) => {
                                    return PaymentMethodArraysMap[v];
                                }}
                                onChange={(v) => onChange({ PaymentMethod: v })}
                                disabled={this.props.hasInternationalBankAccountDestination}
                            />
                        </div>
                    </div>
                )}
                {drawdownType !== DrawdownType.Regular && (
                    <div className="row">
                        <div className="col-4">
                            <DatePicker
                                selected={
                                    data?.NextPayment
                                        ? JsonDateParser.toDateNoOffset(data?.NextPayment)
                                        : specifiedFirstPaymentDateAtLeast
                                }
                                label={labels.firstPaymentDate}
                                {...datePickerOptions}
                                alwaysValidateOnUpdate={true}
                                atLeast={{
                                    errorMessage: firstPaymentDateMoreThanMessage,
                                    value: specifiedFirstPaymentDateAtLeast,
                                }}
                                // disabled={drawdownType === DrawdownType.Regular}
                                filterDate={{
                                    errorMessage: messages.dateOver28,
                                    validator: (v) => v.getDate() <= 28,
                                }}
                                required={messages.firstPaymentDateRequired}
                                onChange={(v) => {
                                    onChange({ NextPayment: JsonDateParser.toJsonDateNoOffset(v) });
                                    if (!this.state.firstPaymentDateAlertDisplay) {
                                        this.setState({
                                            firstPaymentDateAlertDisplay: true,
                                        });
                                    }
                                }}
                            />
                        </div>
                    </div>
                )}
                {drawdownType === DrawdownType.Regular && this.state.firstPaymentDateAlertDisplay && (
                    <Alert message={RegularIncomePaymentAlertMsg.FirstPaymentDate} alertType={AlertType.Warning} />
                )}
                {drawdownType !== DrawdownType.Regular && (
                    <>
                        <div className="row">
                            <div className="col-12">
                                <RadiosEditor
                                    inputGridClasses="w-400 pl-10"
                                    cssWrapper="rg-inline"
                                    label={labels.endDate}
                                    selected={!this.state.endDateDisplay}
                                    options={[true, false]}
                                    getText={(v) => (v ? labels.untilNotified : labels.enterADate)}
                                    onChange={(v) => {
                                        onChange({ PaymentEnd: null });
                                        this.setState({
                                            endDateDisplay: !v,
                                        });
                                    }}
                                />
                            </div>
                        </div>

                        <div className="row w-400">
                            <div className="col-6"></div>
                            <div className="col-6 pl-0">
                                <DatePicker
                                    data-test="endDateDate"
                                    selected={
                                        this.state.endDateDisplay
                                            ? JsonDateParser.toDateNoOffset(data?.PaymentEnd)
                                            : undefined
                                    }
                                    {...datePickerOptions}
                                    alwaysValidateOnUpdate={true}
                                    atLeast={
                                        this.state.endDateDisplay && {
                                            errorMessage: messages.endDateUnder,
                                            value: data?.NextPayment
                                                ? JsonDateParser.toDateNoOffset(data?.NextPayment)
                                                : new Date(),
                                        }
                                    }
                                    required={this.state.endDateDisplay && messages.enterADateRequired}
                                    onChange={(v) => {
                                        onChange({ PaymentEnd: JsonDateParser.toJsonDateNoOffset(v) });
                                    }}
                                    disabled={!this.state.endDateDisplay}
                                    filterDate={
                                        this.state.endDateDisplay && {
                                            errorMessage: messages.dateOver28,
                                            validator: (v) => v?.getDate() <= 28,
                                        }
                                    }
                                />
                            </div>
                        </div>
                    </>
                )}
                {drawdownType !== DrawdownType.Regular && this.state.endDateDisplay && data?.PaymentEnd && (
                    <Alert message={RegularIncomePaymentAlertMsg.EndDate} alertType={AlertType.Warning} />
                )}
                <hr />
                {(<>
                    <EstimatedPAYE 
                        data={this.props.data}
                        drawdownType={this.props.drawdownType}
                        pensionBase={this.props.pensionBase} 
                        onChange={() => {}}
                        onRef={(ref) => {}}
                        processType={this.props.processType}
                        regularCrystallisationDetails={regularCrystallisationDetails}
                    />
                    <hr />
                </>)}
            </>
        );
    }
}

export default RegularIncomePayment;
